/**
 * 🌫️ MistyMap — the player's living discovery board.
 *
 * Every routable area in OMNIMUSCLE is initially hidden behind fog.
 * Visiting an area unlocks it on this map permanently. Categories
 * group the world into thematic regions (Dragonious / Combat / Guild
 * / Economy / Wellness / Lore / Support / Home).
 *
 * Render rules per area card:
 *   • Visited  → full icon + label + link active
 *   • Fogged   → grayscale silhouette + "???" + fog overlay + link still
 *                clickable (you can sail blindly into the fog)
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import useMistyMap from "@/hooks/useMistyMap";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MistyMap() {
  // Visiting the Misty Map itself is its own discovery — meta.
  useMistyMap("misty_map");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/misty-map/state`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Failed to load the Misty Map"));
  }, []);

  const pct = data
    ? Math.round((data.visited_count / data.total_areas) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-950 via-black to-stone-950 text-amber-100">
      <Header />
      <main className="max-w-6xl mx-auto px-4 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-amber-300/70">
              🌫️ Discovery Map
            </div>
            <h1
              className="text-3xl sm:text-5xl font-black mt-1"
              style={{ fontFamily: "Anton, sans-serif" }}
              data-testid="misty-map-title"
            >
              THE MISTY MAP
            </h1>
            <p className="text-xs text-neutral-400 mt-2 italic max-w-2xl">
              Every region of Dragonious begins shrouded in fog. Visit a
              place and it remembers you forever.
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-black text-amber-200" data-testid="misty-map-visited">
              {data?.visited_count ?? 0} / {data?.total_areas ?? 0}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">
              areas discovered ({pct}%)
            </div>
            <div className="mt-2 w-40 h-1.5 bg-stone-900 rounded-full overflow-hidden ml-auto">
              <div
                className="h-full bg-gradient-to-r from-amber-500 to-rose-500 transition-all duration-700"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        </div>

        {err && (
          <div className="mb-6 px-4 py-3 rounded-lg border border-red-700/50 bg-red-950/30 text-red-200 text-sm">
            {err}
          </div>
        )}

        {!data && !err && (
          <div className="text-neutral-500 text-sm italic">Parting the fog…</div>
        )}

        {data?.categories?.map((cat) => (
          <section key={cat.name} className="mb-10" data-testid={`misty-map-cat-${cat.name}`}>
            <div className="flex items-baseline gap-3 mb-3">
              <h2
                className="text-xl font-black text-amber-200 uppercase tracking-wider"
                style={{ fontFamily: "Anton, sans-serif" }}
              >
                {cat.name}
              </h2>
              <span className="text-[10px] text-neutral-500 uppercase tracking-wider">
                {cat.visited} / {cat.total} discovered
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {cat.areas.map((a) => (
                <Link
                  key={a.id}
                  to={a.path}
                  data-testid={`misty-map-area-${a.id}`}
                  className={`relative rounded-xl border-2 p-3 transition-all overflow-hidden ${
                    a.visited
                      ? "border-amber-600/60 bg-gradient-to-br from-stone-900 to-black hover:border-amber-400 hover:scale-[1.03]"
                      : "border-stone-700/40 bg-stone-950 hover:border-stone-500"
                  }`}
                >
                  {/* 🌫️ Fog overlay for unvisited areas */}
                  {!a.visited && (
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-stone-700/40 via-stone-900/80 to-black/90 backdrop-blur-[1px]" />
                  )}

                  <div className="relative z-10 flex flex-col items-center text-center gap-1">
                    <div className={`text-4xl ${a.visited ? "" : "grayscale opacity-30"}`}>
                      {a.visited ? a.icon : "🌫️"}
                    </div>
                    <div
                      className={`text-xs font-black ${
                        a.visited ? "text-amber-100" : "text-neutral-500 italic"
                      }`}
                    >
                      {a.visited ? a.label : "???"}
                    </div>
                    {a.visited ? (
                      <div className="text-[9px] uppercase tracking-widest text-emerald-400/80">
                        ✓ Discovered
                      </div>
                    ) : (
                      <div className="text-[9px] uppercase tracking-widest text-stone-500">
                        🔍 Sail blind
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ))}

        <div className="mt-8 px-4 py-3 rounded-lg border border-amber-900/30 bg-amber-950/10 text-[11px] text-amber-200/70 italic">
          🪶 The fog only lifts when you walk into it. Click any silhouette to sail blind — discoveries are permanent.
        </div>
      </main>
      <Footer />
    </div>
  );
}
